import { Button, Checkbox, Dialog, Icon } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { FormRenderProps, useField, useForm } from 'react-final-form';
import { closeReuploadDialog, useReuploadDialogOpened } from '../../../../services/listerUploadVehicle/v2';
import { useGetVinInfoQuery } from '../../../../services/api/upload-vehicle';
import { auctionTypeOptions } from './AuctionField';
import { Vehicle } from 'types/vehicle';
import { useCurrentUser } from 'hooks/selectors';

const StyledDialog = styled(Dialog)`
  .reupload-vehicle-paper {
    margin: auto 13px;
    box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
    border-radius: 20px;
    width: 100%;
  }

  @media (min-width: 1200px) {
    .reupload-vehicle-paper {
      max-width: 367px;
    }
  }
`;

const Wrapper = styled.form`
  display: flex;
  flex-direction: column;
  padding: 26px 24px 24px;
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.div`
  font-size: 22px;
  font-weight: 500;
  line-height: 25.78px;
  color: #000;
`;

const CloseIcon = styled(Icon)`
  width: 24px;
  height: 24px;
`;

const SubControls = styled.div`
  display: flex;
  margin-top: auto;
  align-items: center;
  justify-content: space-between;
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

const Action = styled(Button)<{ disabled?: boolean }>`
  border-radius: 6px;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25) !important;
  font-size: 16px;
  font-weight: 500;
  line-height: 19px;
  padding-top: 13px;
  padding-bottom: 12px;
  text-transform: none;
  flex: 1 1 100%;
`;

const CancelAction = styled(Action)<{ disabled?: boolean }>`
  background: rgba(217, 217, 217, 1);
  color: rgba(117, 117, 117, 1);
`;

const SubmitAction = styled(Action)<{ disabled?: boolean }>`
  background-color: ${props => (props.disabled ? 'rgba(34, 90, 145, 0.5)' : 'rgba(34, 90, 145, 1)')} !important;
  color: #fff !important;
`;

const VinText = styled.div`
  margin-top: 8px;
  margin-bottom: 28px;
  color: #757575;
  font-size: 12px;
  line-height: 100%;
`;

const MainPart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  font-weight: 400;
  font-size: 18px;
  line-height: 100%;
  letter-spacing: 0%;
  color: #000;

  & > span:last-child {
    color: #757575;
    font-size: 16px;
  }
`;

const TransferPhotosCheckbox = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 100%;
  display: flex;

  align-items: center;

  & > span:first-child {
    padding-left: 0;
    padding-right: 8px;
  }
`;

const NewButton = styled.div`
  display: flex;
  color: #3994de;
  font-size: 16px;
  font-weight: 500;
  word-spacing: 8px;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

type FormValues = {
  seller_id: any;
  vin: string;
  manual_upload: boolean;
  auction_type: {
    label: string;
    value: string;
  };
  is_reupload: boolean;
};

const getAuctionTypeInitialValue = (vehicle?: Vehicle) => {
  if (!vehicle || !vehicle.auction_type) return auctionTypeOptions[0];
  return auctionTypeOptions.find(opt => opt.value === vehicle.auction_type) || auctionTypeOptions[0];
};

export const ReuploadConfirmDialog = () => {
  const dispatch = useDispatch();

  const form = useForm();

  const { input: checkboxInput } = useField('copy_images');

  const toggleCheckbox = () => checkboxInput.onChange(!checkboxInput.value);

  const { isOpened, vin } = useReuploadDialogOpened();

  const { data } = useGetVinInfoQuery(vin!, {
    skip: !vin
  });

  const { duplicateTitle } = data || {};

  const handleClose = () => {
    dispatch(closeReuploadDialog());
  };

  const handleNew = () => {
    form.submit();
    handleClose();
  };
  const handleSubmit = () => {
    form.change('copy_values', true);
    form.submit();
    handleClose();
  };

  const user = useCurrentUser();
  const showTransferPhotos = user.role === 'admin' && user.job_title === 'Admin';

  return (
    <StyledDialog classes={{ paper: 'reupload-vehicle-paper' }} open={isOpened} onBackdropClick={handleClose}>
      <Wrapper>
        <Header>
          <Title>{duplicateTitle}</Title>
          <CloseIcon onClick={handleClose}>close</CloseIcon>
        </Header>
        <VinText>{`VIN ${vin}`}</VinText>
        <MainPart>
          <span>{`You are about to re-upload, the ${duplicateTitle}.`}</span>
          <span>{`Click “Confirm” to fill up the form with the existing data(excluding images) or “NEW” If you want to start from scratch.`}</span>
        </MainPart>
        <SubControls>
          {showTransferPhotos ? (
            <TransferPhotosCheckbox>
              <Checkbox onClick={toggleCheckbox} checked={checkboxInput.value} color="primary" />
              <span>Transfer Photos</span>
            </TransferPhotosCheckbox>
          ) : (
            <div style={{ height: '48px', display: 'flex' }} />
          )}
        </SubControls>

        <ActionsWrapper>
          <CancelAction type="button" onClick={handleClose}>
            Cancel
          </CancelAction>
          <SubmitAction type="button" onClick={handleSubmit}>
            Confirm
          </SubmitAction>
          <SubmitAction disabled={checkboxInput.value} type="button" onClick={handleNew}>
            New
          </SubmitAction>
        </ActionsWrapper>
      </Wrapper>
    </StyledDialog>
  );
};

type FormProps = FormRenderProps<FormValues>;
